import type { CDMOApplication } from './supabase';
import { withAsciiDashes } from '../utils/labelMatching';
import {
  DETAILED_CAPABILITIES_OPTIONS_MAP,
  CDMO_BUSINESS_PROFILE_OPTIONS,
  type CDMOBusinessProfile,
} from '../constants';
import { resolveDetailedCapabilityLabel } from '../constants/detailedCapabilities';

/**
 * Tier 1 technical feasibility (gatekeeper) vs Tier 2 weighted ranking
 *
 * **Contract:** feasibility is evaluated only in {@link passesTier1Gatekeeper} (and alias
 * {@link validateTechnicalFeasibility}). The matchmaking page applies this gate to the full CDMO list
 * *before* computing any Tier 2 percentage scores, so users are not ranked against partners that fail
 * hard legal/technical requirements.
 *
 * **Hard stops (non‑negotiable AND):**
 * - **Specific services / unit operations:** each selected tag must appear **exactly** (canonical string,
 *   after trim) under `detailed_capabilities` for a user-selected product type — see {@link passesDetailedServiceGate}.
 * - **Regulatory certifications:** each requested label must match **exactly** (after trim) an entry in
 *   `gmp_certifications` — see {@link cdmoHasAllExactRegulatoryCertifications}.
 *
 * `GatekeeperFilterState` fields:
 * - {@link GatekeeperFilterState.cdmoPartnerType}: exact match on `CDMOApplication.cdmo_business_profile`
 *   (admin-set on CDMO intake; aligns with “What type of CDMO partner…” on the matching sidebar).
 *
 * Certifications, product type, and specific services are gatekeeper fields (AND).
 * **Company search** is not Tier 1: apply {@link cdmoMatchesCompanySearchQuery} on the matchmaking page
 * after the gatekeeper pass (substring filter only; no tooltip/score row).
 * Development stage, support, batch size, budget, geography, and speed-to-start are Tier 2 (ranking) on the matching page.
 */
export interface GatekeeperFilterState {
  productType: string[];
  detailedServices: string[];
  /** Empty = Flexible / No Preference; otherwise must equal `CDMOApplication.cdmo_business_profile`. */
  cdmoPartnerType: string;
  certifications: string[];
}

/**
 * Builds a lowercase string of CDMO fields used for sidebar company search (not Tier 1).
 */
export function buildCdmoSearchableText(cdmo: CDMOApplication): string {
  return [
    cdmo.company_name,
    cdmo.description,
    cdmo.facility_locations_details,
    ...(cdmo.primary_capabilities || []),
    ...(cdmo.preferred_project_types || []),
    ...(cdmo.gmp_certifications || []),
    ...(cdmo.operating_regions || []),
    ...Object.values(cdmo.detailed_capabilities || {}).flat(),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * Substring match for the “Search Companies” field — applied after Tier 1 gatekeeper only.
 *
 * @param cdmo - CDMO profile
 * @param rawQuery - User search text (trimmed; empty = match all)
 * @returns Whether the CDMO’s searchable text includes the query
 */
export function cdmoMatchesCompanySearchQuery(cdmo: CDMOApplication, rawQuery: string): boolean {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return true;
  return buildCdmoSearchableText(cdmo).includes(q);
}

/**
 * Maps biotech search UI values to CDMO `engagement_scales` values (exact Tier 1 match).
 */
export const DEVELOPMENT_STAGE_GATE_VALUES: Record<string, string[]> = {
  'Preclinical R&D': ['Preclinical R&D'],
  'Phase I': ['Phase I Clinical'],
  'Phase II': ['Phase II Clinical'],
  'Phase III': ['Phase III Clinical'],
  'Commercial Manufacturing': ['Commercial Manufacturing'],
  'Tech Transfer': ['Tech Transfer Support'],
};

/**
 * Project Kickoff timing is deliberately NOT a gatekeeper. Scope §3/§8/§9 require it to remain a
 * soft weighted factor: a CDMO slower than the sponsor's requested window scores lower through
 * scoreSpeedToStart and must never be excluded from the result set on timing alone. The former
 * band filter that dropped slower partners was removed for that reason — do not reintroduce one.
 */


/**
 * Product types that define "biologic / advanced modality" eligibility vs Medical Devices-only.
 */
const NON_DEVICE_PRIMARY_TYPES = new Set([
  'Small Molecule',
  'Biologics',
  'Cell & Gene Therapy',
  'Vaccines',
]);

/**
 * Medical Devices detailed tags that indicate assembly / device-focused work (Tier 1 hard-stop when conflicting with biologic intents).
 *
 * Every Medical Devices capability counts as device-focused work, so this is derived from the
 * modality's option list rather than duplicated. Declaring it by hand let the two drift apart
 * whenever the taxonomy grew: a tag missing here stopped {@link isMedicalDeviceAssemblyTagged}
 * recognising a device-focused CDMO whose `primary_capabilities` do not say "Medical Devices".
 */
export const MEDICAL_DEVICE_ASSEMBLY_TAGS: readonly string[] =
  DETAILED_CAPABILITIES_OPTIONS_MAP['Medical Devices'] ?? [];

/**
 * User selections that trigger exclusion of Medical Devices–only CDMOs for biologic/advanced modality projects.
 * Each row: required product types (all must be selected) + at least one of the services must be selected.
 */
export const BIOLOGIC_MODALITY_GATE_INTENTS: ReadonlyArray<{
  requiredProductTypes: string[];
  triggerServices: string[];
}> = [
  {
    requiredProductTypes: ['Biologics'],
    triggerServices: ['Formulation Development', 'Sterile Fill/Finish — Biologics'],
  },
  {
    requiredProductTypes: ['Cell & Gene Therapy'],
    triggerServices: ['Viral Vector Manufacturing'],
  },
  {
    requiredProductTypes: ['Vaccines'],
    triggerServices: ['mRNA Vaccines'],
  },
];

/**
 * Returns product type keys in {@link DETAILED_CAPABILITIES_OPTIONS_MAP} that list a given service tag.
 *
 * @param service - Specific service label from the map
 * @returns Product type keys that own this tag
 */
export function getProductTypesForService(service: string): string[] {
  const wanted = normalizeDetailedCapabilityLabel(service);
  const keys: string[] = [];
  for (const [productType, services] of Object.entries(DETAILED_CAPABILITIES_OPTIONS_MAP)) {
    if (services.some((s) => normalizeDetailedCapabilityLabel(s) === wanted)) {
      keys.push(productType);
    }
  }
  return keys;
}

/** Case/whitespace/dash folding only — the comparison key after aliases are resolved. */
function foldDetailedCapabilityLabel(value: string): string {
  return withAsciiDashes(value.trim().toLowerCase()).replace(/\s+/g, ' ');
}

/**
 * Canonical comparison key for a detailed-capability label.
 *
 * Resolves pre-expansion labels to their current names via the shared
 * {@link resolveDetailedCapabilityLabel} (also used by `CDMOProfile.tsx` and
 * `sponsorCardDetails.ts`, so all three agree on one alias table and one fold), then folds case,
 * whitespace and typographic dashes so a CDMO whose stored profile still says "Lyophilization" —
 * in any casing — keeps matching "Lyophilization — Biologics". Mirrors {@link
 * normalizeRegulatoryCertificationLabel}.
 *
 * Idempotent: no alias target is itself an alias key, so folding an already-canonical label is a
 * no-op. {@link passesDetailedServiceGate} relies on this — it normalizes, then calls
 * {@link getProductTypesForService}, which normalizes again.
 *
 * Also applied to modality keys and product types by the same call sites. That is safe because no
 * alias key collides with a modality name, and a test pins that invariant.
 */
function normalizeDetailedCapabilityLabel(value: string): string {
  return foldDetailedCapabilityLabel(resolveDetailedCapabilityLabel(value));
}

/**
 * Device tags in comparison form, built once rather than per CDMO.
 *
 * Normalized so a profile still storing a pre-expansion label ("Auto-injectors") resolves to its
 * current tag ("Autoinjectors") and keeps triggering the exclusion. {@link isMedicalDeviceAssemblyTagged}
 * runs for every CDMO in the result set on every filter change, so this stays out of that loop.
 */
const NORMALIZED_MEDICAL_DEVICE_ASSEMBLY_TAGS: ReadonlySet<string> = new Set(
  MEDICAL_DEVICE_ASSEMBLY_TAGS.map((tag) => normalizeDetailedCapabilityLabel(tag))
);

/**
 * CDMO has only Medical Devices as primary modality (no small-molecule / biologic / vaccine / CGT).
 *
 * @param cdmo - CDMO application
 * @returns True if the CDMO is classified as Medical Devices–only at the primary level
 */
export function isMedicalDevicesOnlyPrimary(cdmo: CDMOApplication): boolean {
  const pc = cdmo.primary_capabilities || [];
  if (pc.length === 0) return false;
  const hasNonDevice = pc.some((p) => NON_DEVICE_PRIMARY_TYPES.has(p));
  return pc.includes('Medical Devices') && !hasNonDevice;
}

/**
 * CDMO is tagged for medical device assembly–class services (under Medical Devices) without biologic modalities.
 *
 * Complements {@link isMedicalDevicesOnlyPrimary} rather than repeating it: this one catches a
 * device-focused CDMO whose `primary_capabilities` do not name "Medical Devices" — an empty list, or
 * one holding only values outside the five canonical modalities — which the primary-level check
 * rejects outright. It previously ended by delegating back to that check, which made the whole tag
 * comparison redundant (`A || (B && A)` is just `A`) and this function unable to catch anything on
 * its own.
 *
 * @param cdmo - CDMO application
 * @returns True if assembly-like device tags are present without non-device primary types
 */
export function isMedicalDeviceAssemblyTagged(cdmo: CDMOApplication): boolean {
  const deviceServices = cdmo.detailed_capabilities?.['Medical Devices'] || [];
  const hasAssemblyClass = deviceServices.some((s) =>
    NORMALIZED_MEDICAL_DEVICE_ASSEMBLY_TAGS.has(normalizeDetailedCapabilityLabel(String(s)))
  );
  if (!hasAssemblyClass) return false;
  // A CDMO that also claims a biologic / small-molecule / vaccine / CGT primary is not device-only,
  // so its device tags must not exclude it from a biologic search.
  return !(cdmo.primary_capabilities || []).some((p) => NON_DEVICE_PRIMARY_TYPES.has(p));
}

/**
 * True when the user's gatekeeper selections represent a biologic/advanced-modality project that must not return Medical Devices–only CDMOs.
 *
 * @param filters - Gatekeeper filter slice
 * @returns Whether biologic-modality exclusion applies
 */
export function hasBiologicModalityConflictIntent(filters: GatekeeperFilterState): boolean {
  // Normalized so a saved search still holding a pre-expansion label ("Sterile Fill/Finish")
  // triggers the same intent as the current one ("Sterile Fill/Finish — Biologics").
  const selectedServices = new Set(
    filters.detailedServices.map((s) => normalizeDetailedCapabilityLabel(s))
  );
  return BIOLOGIC_MODALITY_GATE_INTENTS.some((intent) => {
    const hasAllPts = intent.requiredProductTypes.every((pt) =>
      filters.productType.includes(pt)
    );
    const hasService = intent.triggerServices.some((s) =>
      selectedServices.has(normalizeDetailedCapabilityLabel(s))
    );
    return hasAllPts && hasService;
  });
}

/**
 * When biologic-modality intent is active, exclude CDMOs that are Medical Devices–only or device-assembly–tagged without biologic primaries.
 *
 * @param filters - Gatekeeper filters
 * @param cdmo - CDMO application
 * @returns True if this CDMO must be excluded
 */
export function shouldExcludeForDeviceVsBiologicConflict(
  filters: GatekeeperFilterState,
  cdmo: CDMOApplication
): boolean {
  if (!hasBiologicModalityConflictIntent(filters)) return false;
  return isMedicalDevicesOnlyPrimary(cdmo) || isMedicalDeviceAssemblyTagged(cdmo);
}

/**
 * Tier 1: specific service / unit-operation tag must appear **exactly** in `detailed_capabilities`
 * (canonical label from {@link DETAILED_CAPABILITIES_OPTIONS_MAP}, compared with trim). Substrings or fuzzy
 * matches do not pass.
 *
 * @param cdmo - CDMO application
 * @param service - Service tag (must match stored tag exactly)
 * @param selectedProductTypes - User-selected product types
 * @returns True if the CDMO passes the gate for this service
 */
export function passesDetailedServiceGate(
  cdmo: CDMOApplication,
  service: string,
  selectedProductTypes: string[]
): boolean {
  const normalizedService = normalizeDetailedCapabilityLabel(service);
  if (!normalizedService) return false;

  const owningTypes = getProductTypesForService(normalizedService);
  const selectedTypes = new Set(
    selectedProductTypes.map((pt) => normalizeDetailedCapabilityLabel(pt))
  );
  const relevantKeys = owningTypes.filter((k) =>
    selectedTypes.has(normalizeDetailedCapabilityLabel(k))
  );
  if (relevantKeys.length === 0) return false;

  const detailed = cdmo.detailed_capabilities || {};
  const detailedByNormalizedKey = new Map<string, string[]>();
  for (const [key, list] of Object.entries(detailed)) {
    if (!Array.isArray(list)) continue;
    detailedByNormalizedKey.set(
      normalizeDetailedCapabilityLabel(key),
      list.map((tag) => normalizeDetailedCapabilityLabel(String(tag)))
    );
  }

  return relevantKeys.some((key) => {
    const list = detailedByNormalizedKey.get(normalizeDetailedCapabilityLabel(key));
    return (
      Array.isArray(list) &&
      list.some((tag) => tag === normalizedService)
    );
  });
}

/**
 * Whether the CDMO business-profile dropdown requests a Tier 1 exact match on
 * {@link CDMOApplication.cdmo_business_profile}.
 *
 * @param cdmoPartnerType - Raw filter value (empty = Flexible / No Preference)
 * @returns True when a known profile value is selected
 */
export function isTier1CdmoBusinessProfileFilterActive(cdmoPartnerType: string): boolean {
  return (
    Boolean(cdmoPartnerType) &&
    CDMO_BUSINESS_PROFILE_OPTIONS.includes(cdmoPartnerType as CDMOBusinessProfile)
  );
}

/**
 * @param cdmoPartnerType - Same as {@link isTier1CdmoBusinessProfileFilterActive}
 */
export const isTier1PartnerTypeFilterActive = isTier1CdmoBusinessProfileFilterActive;

/**
 * Maps legacy CDMO intake labels and pre-launch filter labels to canonical certification keys.
 * Exported for the CSV bulk upload, which matches these labels ignoring case and spacing.
 */
export const LEGACY_CERTIFICATION_ALIASES: Record<string, string> = {
  'FDA cGMP': 'US FDA cGMP',
  'US FDA cGMP': 'US FDA cGMP',
  'EMA GMP': 'EU GMP',
  'EU GMP': 'EU GMP',
  'ISO 13485 (Medical Devices)': 'ISO 13485, if device or combination-product related',
  'ISO 13485 (Devices)': 'ISO 13485, if device or combination-product related',
  'ISO 13485, if device or combination-product related': 'ISO 13485, if device or combination-product related',
  'DEA Registration': 'DEA Registration, if controlled substances apply',
  'DEA Registration (for controlled substances)': 'DEA Registration, if controlled substances apply',
  'DEA Registration, if controlled substances apply': 'DEA Registration, if controlled substances apply',
  'Health Canada': 'Health Canada',
  'TGA (Australia)': 'TGA / Australia',
  'TGA / Australia': 'TGA / Australia',
  'PMDA (Japan)': 'PMDA / Japan',
  'PMDA / Japan': 'PMDA / Japan',
  'Other Regional': 'Other regional requirements',
  'Other regional requirements': 'Other regional requirements',
  'PMDA (Japan), TGA (Australia), Other': 'Other regional requirements',
};

/** The alias table in lookup form, built once at module load. */
const CERTIFICATION_ALIAS_MAP: ReadonlyMap<string, string> = new Map(
  Object.entries(LEGACY_CERTIFICATION_ALIASES)
);

export function normalizeRegulatoryCertificationLabel(value: string): string {
  const trimmed = String(value ?? '').trim();
  // Map rather than a bare object index: a stored certification of 'toString' would otherwise
  // resolve up the prototype chain to a function, which `??` does not treat as missing.
  return CERTIFICATION_ALIAS_MAP.get(trimmed) ?? trimmed;
}

/**
 * Tier 1 regulatory gate: every requested certification must match **exactly** (after trim) an entry in
 * `gmp_certifications`. No partial or fuzzy matches — legal compliance requirement.
 *
 * @param cdmo - CDMO application
 * @param requested - Certification labels from the matchmaking sidebar
 * @returns True if the CDMO lists every requested label exactly
 */
export function cdmoHasAllExactRegulatoryCertifications(
  cdmo: CDMOApplication,
  requested: readonly string[]
): boolean {
  const held = new Set(
    (cdmo.gmp_certifications ?? []).map((c) => normalizeRegulatoryCertificationLabel(String(c)))
  );
  return requested.every((req) => {
    const want = normalizeRegulatoryCertificationLabel(req);
    if (!want) return true;
    return held.has(want);
  });
}

/**
 * Human-readable labels for every Tier-1 gatekeeper criterion the CDMO does not satisfy vs the
 * user's filters. Used for Close Alternatives so multiple simultaneous mismatches (e.g. partner
 * profile + certifications) all appear, not just the relaxation path that admitted the CDMO.
 *
 * @param cdmo - Candidate close-alternative CDMO
 * @param filters - Strict gatekeeper slice from the matching sidebar
 * @returns Non-empty rows for partner-type and/or certification gaps when those filters are active
 */
export type CloseAlternativeRelaxedConstraint = {
  key: 'Partner Type' | 'Regulatory Certifications';
  value: string;
};

export function buildCloseAlternativeRelaxedConstraints(
  cdmo: CDMOApplication,
  filters: GatekeeperFilterState
): CloseAlternativeRelaxedConstraint[] {
  const out: CloseAlternativeRelaxedConstraint[] = [];
  if (isTier1CdmoBusinessProfileFilterActive(filters.cdmoPartnerType)) {
    const required = filters.cdmoPartnerType as CDMOBusinessProfile;
    const profile = cdmo.cdmo_business_profile;
    if (!profile || profile !== required) {
      out.push({
        key: 'Partner Type',
        value: profile || 'Not specified',
      });
    }
  }
  if (filters.certifications.length > 0) {
    const held = new Set(
      (cdmo.gmp_certifications ?? []).map((c) => normalizeRegulatoryCertificationLabel(String(c)))
    );
    const missingCerts = filters.certifications.filter(
      (cert) => !held.has(normalizeRegulatoryCertificationLabel(cert))
    );
    if (missingCerts.length > 0) {
      out.push({
        key: 'Regulatory Certifications',
        value: `Missing ${missingCerts.join(', ')}`,
      });
    }
  }
  return out;
}

/**
 * Whether any Tier 1 gatekeeper field is active (used to decide if gate logic runs).
 *
 * @param filters - Gatekeeper filter slice
 * @returns True if at least one gatekeeper field is set
 */
export function hasActiveGatekeeperFilters(filters: GatekeeperFilterState): boolean {
  return (
    filters.productType.length > 0 ||
    filters.detailedServices.length > 0 ||
    isTier1CdmoBusinessProfileFilterActive(filters.cdmoPartnerType) ||
    filters.certifications.length > 0
  );
}

/**
 * Tier 1 hard stop (technical feasibility): CDMO must pass every selected gatekeeper criterion (AND).
 * Failing any criterion excludes the CDMO entirely from match results — **before** Tier 2 weighted scores.
 *
 * @param cdmo - CDMO application
 * @param filters - Gatekeeper filter slice
 * @returns True if the CDMO may proceed to ranking
 */
export function passesTier1Gatekeeper(
  cdmo: CDMOApplication,
  filters: GatekeeperFilterState
): boolean {
  if (!hasActiveGatekeeperFilters(filters)) {
    return true;
  }

  // Medical Devices vs biologic modality (hard exclusion before other checks)
  if (shouldExcludeForDeviceVsBiologicConflict(filters, cdmo)) {
    return false;
  }

  // Product types: every selected type must appear exactly on the CDMO (binary pass/fail)
  for (const pt of filters.productType) {
    if (!cdmo.primary_capabilities?.includes(pt)) {
      return false;
    }
  }

  // Specific services: tightly linked to selected product types via DETAILED_CAPABILITIES_OPTIONS_MAP
  for (const service of filters.detailedServices) {
    if (!passesDetailedServiceGate(cdmo, service, filters.productType)) {
      return false;
    }
  }

  // CDMO business profile (Tier 1): exact match on the admin-only `cdmo_business_profile` column
  // (same values as the “CDMO Business Profile *” admin form control). No implicit default—must match stored value.
  if (isTier1CdmoBusinessProfileFilterActive(filters.cdmoPartnerType)) {
    const required = filters.cdmoPartnerType as CDMOBusinessProfile;
    const profile = cdmo.cdmo_business_profile;
    if (!profile || profile !== required) {
      return false;
    }
  }

  if (!cdmoHasAllExactRegulatoryCertifications(cdmo, filters.certifications)) {
    return false;
  }

  return true;
}

/**
 * Matchmaking engine: validate technical feasibility (Tier 1 hard stops) before any weighted scores.
 * Alias of {@link passesTier1Gatekeeper}.
 */
export const validateTechnicalFeasibility = passesTier1Gatekeeper;

/** Single tooltip row: equal display weight for gatekeeper-only (informational) breakdown. */
export type GatekeeperTooltipRow = { earned: number; max: number; details?: string };

/** Subset of match tooltip keys populated from Tier 1 filters. */
export type GatekeeperTooltipBreakdownPatch = Partial<{
  productType: GatekeeperTooltipRow;
  detailedServices: GatekeeperTooltipRow;
  cdmoPartnerProfile: GatekeeperTooltipRow;
  certifications: GatekeeperTooltipRow;
}>;

/**
 * Builds informational tooltip rows for active gatekeeper criteria. Intended when Tier 2
 * contributes no points: every listed CDMO already passed these checks. Rows use 1/1 display
 * weight and must not be mixed into Tier 2 percentage math.
 *
 * @param gatekeeper - Gatekeeper filter slice from the matching sidebar
 * @returns Key-value rows to merge into the score breakdown for the UI tooltip
 */
export function getGatekeeperTooltipBreakdownRows(
  gatekeeper: GatekeeperFilterState
): GatekeeperTooltipBreakdownPatch {
  const unit = 1;
  const patch: GatekeeperTooltipBreakdownPatch = {};

  if (gatekeeper.productType.length > 0) {
    patch.productType = {
      earned: unit,
      max: unit,
      details: `Lists all selected modalities: ${gatekeeper.productType.join(', ')}`,
    };
  }

  if (gatekeeper.detailedServices.length > 0) {
    patch.detailedServices = {
      earned: unit,
      max: unit,
      details: `Offers selected services: ${gatekeeper.detailedServices.join(', ')}`,
    };
  }

  if (isTier1CdmoBusinessProfileFilterActive(gatekeeper.cdmoPartnerType)) {
    patch.cdmoPartnerProfile = {
      earned: unit,
      max: unit,
      details: `CDMO Business Profile matches: ${gatekeeper.cdmoPartnerType}`,
    };
  }

  if (gatekeeper.certifications.length > 0) {
    patch.certifications = {
      earned: unit,
      max: unit,
      details: `Holds required certifications: ${gatekeeper.certifications.join(', ')}`,
    };
  }

  return patch;
}
